import React, { useState, useEffect, useRef, useCallback } from "react";
import { Grid, Modal, Box } from "@mui/material";
import AlertHeader from "../AlertHeader";
import NotificationForm from "./NotificationForm";
import { Formik } from "formik";
import CustomAccordion from "../CustomAccordion/CustomAccordion";
import * as yup from "yup";
import DeleteModal from "../../../shared/DeleteModal";
import { useDispatch } from "react-redux";
import usePrompt from "../../../Hooks/usePrompt";
import { useNavigate } from "react-router-dom";
import SaveChangesModal from "../../../shared/SaveChangesModal";
import {
  updateAlert,
  deleteAlert,
  updateDefault,
  CreateAlert,
  getAlerts,
} from "../../state/actions";

const NotificationSection = ({
  sectionName,
  sectionNotification,
  error,
  success,
  loadAlerts,
}) => {
  const [notifications, setNotifications] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [expanded, setExpanded] = useState();
  const [deleteTemplate, setDeleteTemplate] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);

  const Navigate = useNavigate();

  const handleBlockedNavigation = useCallback(
    (nextLocation) => {
      if (nextLocation.pathname === "/admin-tool/alerts-notifications")
        return true;
      if (dirty) {
        setPendingLocation(nextLocation.pathname);
        setIsConfirmationModalOpen(true);
        return false;
      }
      return true;
    },
    [dirty, setIsConfirmationModalOpen]
  );

  usePrompt(handleBlockedNavigation, dirty);

  //close navigation blocker handler
  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setPendingLocation(null);
  };

  //discard changes action handler
  const handleDiscardChanges = () => {
    setIsConfirmationModalOpen(false);
    setDirty(false);
  };

  //navigate to pendingLocation effect
  useEffect(() => {
    if (pendingLocation && !dirty) Navigate(pendingLocation);
  }, [Navigate, pendingLocation, dirty]);

  const dispatch = useDispatch();

  const formRef = useRef();
  const handleDeleteAction = () => {
    dispatch(deleteAlert(deleteTemplate, success, error)).then(() => {
      setOpenDeleteModal(false);
      setDeleteTemplate(null);
    });
  };

  useEffect(() => {
    if (showAll) setNotifications(sectionNotification);
    else setNotifications(sectionNotification?.slice(0, 4));
  }, [showAll, sectionNotification]);

  const setDefault = (e, notification) => {
    e.stopPropagation();
    if (notification.isDefault) return;

    let newMenu = JSON.parse(JSON.stringify(notifications));

    const newMenus = newMenu.map((el) => {
      if (el.id === notification.id) {
        return {
          ...el,
          isDefault: true,
        };
      } else {
        return {
          ...el,
          isDefault: false,
        };
      }
    });

    dispatch(updateDefault(notification, success, error)).then(() => {
      dispatch(getAlerts(false));
    });

    if (showAll) setNotifications(newMenus);
    else setNotifications(newMenus?.slice(0, 4));
  };

  const handleDelete = (group, index) => {
    setDeleteTemplate(group);
    setOpenDeleteModal(true);
  };

  const handleCloneTemplate = (group, index) => {
    let text = {
      ...group,
      id: Date.now(),
      name: `New Notification Template (copy ${Date.now()
        .toString()
        .slice(7, 10)})`,
    };
    dispatch(CreateAlert(text, success, error)).then(() => {
      dispatch(getAlerts(false));
    });
    /*  
          const newDisputeRules = [...formRef.current.values.templates];
          const newRule = { ...formRef.current.values.templates[index] };
          newRule.id = Math.floor(Math.random() * 50000);
          newRule.name = `Copy of ${newDisputeRules[index].name}`;
          
          newDisputeRules.push(newRule);
         if(newDisputeRules[index].default){
          formRef.current.values.templates[index].default = false
          formRef.current.setFieldValue("templates", newDisputeRules);
         }
         else {
          formRef.current.setFieldValue("templates", newDisputeRules);
         } */
  };

  const AlertMenu = [
    {
      id: 2,
      action: "Duplicate",
      handler: handleCloneTemplate,
    },

    {
      id: 3,
      action: "Delete",
      handler: handleDelete,
    },
  ];

  const handleSaveChanges = (values) => {
    console.log(values);
    dispatch(updateAlert(values, success, error)).then(() => {
      setDirty(false);
      setIsConfirmationModalOpen(false);
    });
  };

  const validationSchema = yup.object().shape({
    text: yup.string().required("required field"),
  });

  return (
    <div>
      <SaveChangesModal
        open={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        discardChanges={handleDiscardChanges}
        saveChanges={() => formRef.current.submitForm()}
      />

      <Modal
        open={openDeleteModal}
        handleClose={() => setOpenDeleteModal(false)}
      >
        <DeleteModal
          title="Permenantly Delete Template"
          content={
            "Are you sure you want to delete Template? data will no longer be restored."
          }
          handleCloseModal={() => setOpenDeleteModal(false)}
          action={handleDeleteAction}
        />
      </Modal>

      <AlertHeader headline={sectionName} filter={false} />
      <Grid container direction="column" spacing={1} marginBottom={10}>
        <>
          <Grid item xs={12}>
            {notifications &&
              React.Children.toArray(
                notifications.map((notification, index) => (
                  <Formik
                    key={notification.id}
                    innerRef={formRef}
                    initialValues={{
                      ...notification,
                    }}
                    validationSchema={validationSchema}
                    enableReinitialize={true}
                    onSubmit={(values, { setSubmitting }) => {
                      setSubmitting(false);
                      handleSaveChanges(values);
                    }}
                    validateOnChange={false}
                    validateOnBlur={false}
                  >
                    {({ ...formikProps }) => {
                      const {
                        errors,
                        values,
                        submitForm,
                        setFieldValue,
                        setValues,
                      } = formikProps;

                      return (
                        <>
                          <CustomAccordion
                            menu={AlertMenu}
                            groupName={notification.name}
                            group={notification}
                            makeasDefault={setDefault}
                            key={index}
                            index={index}
                            alerts={notifications}
                            values={values}
                            setExpanded={setExpanded}
                            expanded={expanded}
                            setDirty={setDirty}
                            setValues={setValues}
                            action={() => submitForm()}
                          >
                            <NotificationForm
                              index={index}
                              setFieldValue={setFieldValue}
                              alert={notification}
                              values={values}
                              disputeRules={notifications}
                              errors={errors}
                              setValues={setValues}
                              action={() => submitForm()}
                              success={success}
                              error={error}
                              setIsConfirmationModalOpen={
                                setIsConfirmationModalOpen
                              }
                              dirty={dirty}
                              setDirty={setDirty}
                              isConfirmationModalOpen={isConfirmationModalOpen}
                              setPendingLocation={setPendingLocation}
                              pendingLocation={pendingLocation}
                            />
                          </CustomAccordion>
                        </>
                      );
                    }}
                  </Formik>
                ))
              )}
          </Grid>
        </>

        <Box
          display="inline"
          component="div"
          sx={{
            color: "#007AFF",
            fontSize: ".875rem",
            lineHeight: "16px",
            cursor: "pointer !important",
            textDecoration: "underline",
            textDecorationColor: "#007AFF",
            marginTop: "10px",
            width: "fit-content",
          }}
          onClick={() => setShowAll(!showAll)}
        >
          {!showAll ? `Show all templates` : "Hide templates"}
        </Box>
      </Grid>
    </div>
  );
};

export default NotificationSection;
